import React, { useState, useEffect, useMemo } from "react";
import axios from "../../axiosConfig";
import dayjs from "dayjs";
import "../../styles/BookTrainingModal.css";

const BookTrainingModal = ({ isOpen, onClose, refreshCalendar, trainerId, schoolId, slotTime }) => {
    const [dogs, setDogs] = useState([]);
    const [filteredDogs, setFilteredDogs] = useState([]);
    const [selectedDog, setSelectedDog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    // Selected time state
    const [selectedStartTime, setSelectedStartTime] = useState(slotTime);

    // Halls
    const [halls, setHalls] = useState([]);
    const [selectedHallId, setSelectedHallId] = useState(null);

    // Offered Services
    const [offeredServices, setOfferedServices] = useState([]);
    const [selectedOfferedServiceId, setSelectedOfferedServiceId] = useState(null);

    // Working hours for time selection
    const [workingHoursForDay, setWorkingHoursForDay] = useState(null);

    // Reset state when modal opens with new slot
    useEffect(() => {
        if (isOpen && slotTime) {
            setSelectedStartTime(slotTime);
            setSelectedDog(null);
            setSearchTerm("");
            setError("");
        }
    }, [isOpen, slotTime]);

    // Generate available time slots based on trainer's working hours or fallback
    const availableTimeSlots = useMemo(() => {
        if (!slotTime) return [];

        const slots = [];
        let startHour, startMinute, endHour, endMinute;

        if (workingHoursForDay) {
            // Use actual working hours
            [startHour, startMinute] = workingHoursForDay.openingTime.split(':').map(Number);
            [endHour, endMinute] = workingHoursForDay.closingTime.split(':').map(Number);
        } else {
            // Fallback: generate slots around the clicked time (3 hours before and after)
            const clickedHour = slotTime.hour();
            startHour = Math.max(6, clickedHour - 3);
            startMinute = 0;
            endHour = Math.min(22, clickedHour + 4);
            endMinute = 0;
        }

        let currentTime = slotTime.clone().hour(startHour).minute(startMinute);
        const endTime = slotTime.clone().hour(endHour).minute(endMinute);

        while (currentTime.isBefore(endTime)) {
            slots.push(currentTime.clone());
            currentTime = currentTime.add(15, 'minute'); // 15-minute intervals
        }

        return slots;
    }, [slotTime, workingHoursForDay]);

    // Fetch trainer's working hours
    useEffect(() => {
        if (!isOpen || !trainerId || !slotTime) return;

        const token = localStorage.getItem("jwt");

        axios.get(`/api/v1/trainer/get-working-hours/${trainerId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => {
                const workingHours = response.data || [];
                const dayOfWeek = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][slotTime.day()];
                const dayHours = workingHours.find(wh => wh.dayOfWeek === dayOfWeek);
                setWorkingHoursForDay(dayHours || null);
            })
            .catch((err) => {
                console.error("Error fetching working hours:", err);
                setWorkingHoursForDay(null);
            });
    }, [isOpen, trainerId, slotTime]);

    // Fetch dogs for booking
    useEffect(() => {
        if (!isOpen || !schoolId) return;
        setLoading(true);
        const token = localStorage.getItem("jwt");
        axios
            .get(`/api/v1/dog/list/${schoolId}/booking`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                const dogsData = response.data || [];
                setDogs(dogsData);
                setFilteredDogs(dogsData);
                setError("");
            })
            .catch(() => {
                setError("Nie udało się pobrać listy psów.");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [isOpen, schoolId]);

    // Fetch available halls for the school with time parameters
    useEffect(() => {
        if (!isOpen || !schoolId || !selectedStartTime) return;
        const token = localStorage.getItem("jwt");

        // Calculate timeFrom and timeTo for the slot
        const timeFrom = selectedStartTime.format("YYYY-MM-DDTHH:mm:ss");
        const timeTo = selectedStartTime.clone().add(1, "hour").format("YYYY-MM-DDTHH:mm:ss");

        axios
            .get(`/api/v1/school/${schoolId}/halls/available`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    timeFrom,
                    timeTo
                }
            })
            .then((res) => {
                const hallsData = res.data || [];
                setHalls(hallsData);
                if (hallsData.length > 0 && !selectedHallId) {
                    setSelectedHallId(hallsData[0].id);
                }
            })
            .catch((err) => {
                console.error("Error fetching available halls:", err);
            });
    }, [isOpen, schoolId, selectedStartTime, selectedHallId]);

    // Fetch offered services for the school
    useEffect(() => {
        if (!isOpen || !schoolId) return;
        const token = localStorage.getItem("jwt");
        axios
            .get(`/api/v1/services/school/${schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                const servicesData = res.data || [];
                setOfferedServices(servicesData);
                if (servicesData.length > 0) {
                    setSelectedOfferedServiceId(servicesData[0].id);
                } else {
                    setSelectedOfferedServiceId(null);
                }
            })
            .catch((err) => {
                console.error("Error fetching offered services:", err);
            });
    }, [isOpen, schoolId]);

    // Filter dogs as user types in the search bar
    useEffect(() => {
        const filtered = dogs.filter((dog) =>
            dog.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredDogs(filtered);
    }, [searchTerm, dogs]);

    // Handle time change
    const handleTimeChange = (e) => {
        const [hour, minute] = e.target.value.split(':').map(Number);
        const newTime = selectedStartTime.clone().hour(hour).minute(minute);
        setSelectedStartTime(newTime);
    };

    const handleSave = () => {
        if (!selectedDog) return;
        const token = localStorage.getItem("jwt");

        // Use selected start time
        const startTimeStr = selectedStartTime.format("YYYY-MM-DDTHH:mm:ss");
        const endTimeStr = selectedStartTime.clone().add(1, "hour").format("YYYY-MM-DDTHH:mm:ss");

        const requestBody = {
            trainerId,
            dogId: selectedDog.id,
            hallId: halls.length > 0 ? selectedHallId : null,
            serviceId: offeredServices.length > 0 ? selectedOfferedServiceId : null,
            startTime: startTimeStr,
            endTime: endTimeStr,
        };

        axios
            .post("/api/v1/training/trainer/book-training", requestBody, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                alert("Trening umówiony pomyślnie!");
                refreshCalendar();
                onClose();
            })
            .catch(() => {
                setError("Błąd przy umawianiu treningu.");
            });
    };

    if (!isOpen) return null;

    return (
        <div className="book-training-modal-overlay">
            <div className="book-training-modal-content">
                <div className="book-training-modal-header">
                    <div className="book-training-modal-header-info">
                        <h2 className="book-training-modal-title">Umów trening</h2>
                        <div className="book-training-slot-time-info">
                            <span className="book-training-slot-time">{selectedStartTime.format("HH:mm")}</span>
                            <span className="book-training-slot-day">{selectedStartTime.format("dddd, DD MMMM")}</span>
                        </div>
                    </div>
                    <button className="book-training-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="book-training-modal-body">
                    {/* Time Selection - Always visible */}
                    <div className="book-training-form-section">
                        <label htmlFor="time-select" className="book-training-form-label">
                            Godzina rozpoczęcia:
                        </label>
                        <select
                            id="time-select"
                            value={selectedStartTime.format("HH:mm")}
                            onChange={handleTimeChange}
                            className="book-training-form-select"
                        >
                            {availableTimeSlots.map((slot) => (
                                <option key={slot.format("HH:mm")} value={slot.format("HH:mm")}>
                                    {slot.format("HH:mm")}
                                </option>
                            ))}
                        </select>
                        {!workingHoursForDay && (
                            <div className="book-training-time-hint" style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                marginTop: '4px'
                            }}>
                                Wybierz godzinę rozpoczęcia treningu
                            </div>
                        )}
                    </div>

                    {/* Hall Dropdown: show only if halls exist */}
                    {halls.length > 0 && (
                        <div className="book-training-form-section">
                            <label htmlFor="hall-select" className="book-training-form-label">
                                Wybierz salę:
                            </label>
                            <select
                                id="hall-select"
                                value={selectedHallId || ""}
                                onChange={(e) => setSelectedHallId(e.target.value)}
                                className="book-training-form-select"
                            >
                                {halls.map((hall) => (
                                    <option key={hall.id} value={hall.id}>
                                        {hall.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Offered Service Dropdown */}
                    {offeredServices.length > 0 && (
                        <div className="book-training-form-section">
                            <label htmlFor="service-select" className="book-training-form-label">
                                Wybierz usługę:
                            </label>
                            <select
                                id="service-select"
                                value={selectedOfferedServiceId || ""}
                                onChange={(e) => setSelectedOfferedServiceId(e.target.value)}
                                className="book-training-form-select"
                            >
                                {offeredServices.map((service) => (
                                    <option key={service.id} value={service.id}>
                                        {service.serviceName}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Search Section */}
                    <div className="book-training-search-section">
                        <label className="book-training-search-label">Wyszukaj psa:</label>
                        <input
                            type="text"
                            className="book-training-search-bar"
                            placeholder="Wpisz nazwę psa..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Dogs Section */}
                    <div className="book-training-dogs-section">
                        <div className="book-training-dogs-header">
                            <span className="book-training-dogs-count">
                                {filteredDogs.length} dostępne psy
                            </span>
                        </div>

                        {loading ? (
                            <div className="book-training-loading">
                                Ładowanie listy psów...
                            </div>
                        ) : error ? (
                            <div className="book-training-error">{error}</div>
                        ) : (
                            <div className="book-training-dogs-list">
                                {filteredDogs.length === 0 ? (
                                    <div className="book-training-no-dogs">
                                        Brak dostępnych psów.
                                    </div>
                                ) : (
                                    filteredDogs.map((dog) => (
                                        <label
                                            key={dog.id}
                                            className={`book-training-dog-option ${selectedDog?.id === dog.id ? 'selected' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name="dog"
                                                value={dog.id}
                                                checked={selectedDog?.id === dog.id}
                                                onChange={() => setSelectedDog(dog)}
                                                className="book-training-dog-radio"
                                            />
                                            <div className="book-training-dog-info">
                                                <div className="book-training-dog-name">{dog.name}</div>
                                                <div className="book-training-dog-details">
                                                    {dog.breed} • {dog.ownersName}
                                                </div>
                                            </div>
                                        </label>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="book-training-modal-footer">
                    <button className="book-training-cancel-btn" onClick={onClose}>
                        Anuluj
                    </button>
                    <button
                        className="book-training-save-btn"
                        onClick={handleSave}
                        disabled={!selectedDog || loading}
                    >
                        Umów trening
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BookTrainingModal;